import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Autocomplete,
  CircularProgress,
  Avatar,
  Skeleton,
  LinearProgress,
  alpha,
} from '@mui/material';
import { Delete, AddOutlined, SmartToyOutlined } from '@mui/icons-material';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatDateTime } from '../utils/datetime';
import { AdminUser } from '../types';

function debounce<T extends (...args: any[]) => void>(fn: T, ms: number): T {
  let timer: ReturnType<typeof setTimeout>;
  return ((...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as T;
}

const roleLabel: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  viewer: 'Viewer',
  app_viewer: 'App Viewer',
};

type DirUser = { id: string; name: string; email: string; photo?: string | null };

const AdminUsersPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({
    open: false, msg: '', severity: 'success',
  });
  const [formData, setFormData] = useState({ email: '', name: '', role: 'viewer', ms_user_id: '' });
  const [dirOptions, setDirOptions] = useState<DirUser[]>([]);
  const [dirLoading, setDirLoading] = useState(false);
  const [dirValue, setDirValue] = useState<DirUser | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchDir = useCallback(
    debounce(async (q: string) => {
      if (q.length < 2) return;
      setDirLoading(true);
      try {
        setDirOptions(await apiService.searchDirectory(q));
      } catch (error: any) {
        setDirOptions([]);
        // Previously swallowed silently — an expired token, a missing Azure
        // AD permission, or a Graph API error all looked identical to "zero
        // matches," with no way to tell which one actually happened.
        showSnack(error.response?.data?.detail || 'Failed to search directory', 'error');
      } finally {
        setDirLoading(false);
      }
    }, 400),
    [],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAdmins(); }, []);

  const loadAdmins = async () => {
    setTableLoading(true);
    try {
      const data = await apiService.listAdmins();
      setAdmins(Array.isArray(data) ? data : (data?.admins || []));
    } catch {
      showSnack('Failed to load admin users', 'error');
    } finally {
      setTableLoading(false);
    }
  };

  const showSnack = (msg: string, severity: 'success' | 'error' = 'success') => {
    setSnack({ open: true, msg, severity });
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await apiService.createAdmin(formData);
      setOpenCreate(false);
      setFormData({ email: '', name: '', role: 'viewer', ms_user_id: '' });
      setDirValue(null);
      setDirOptions([]);
      showSnack('Admin user created');
      loadAdmins();
    } catch (error: any) {
      showSnack(error.response?.data?.detail || 'Failed to create admin user', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateRole = async (email: string, role: string) => {
    try {
      await apiService.updateAdmin(email, { role });
      showSnack('Role updated');
      loadAdmins();
    } catch (error: any) {
      showSnack(error.response?.data?.detail || 'Failed to update role', 'error');
    }
  };

  // App viewers have no admin_users row yet — promoting one to a platform
  // role must CREATE that row (POST), not PATCH one that doesn't exist.
  const handleRoleChange = async (admin: AdminUser, newRole: string) => {
    if (newRole === admin.role) return;
    if (admin.role === 'app_viewer') {
      try {
        await apiService.createAdmin({ email: admin.email, name: admin.name, role: newRole, ms_user_id: '' });
        showSnack(`Promoted to ${roleLabel[newRole] || newRole}`);
        loadAdmins();
      } catch (error: any) {
        showSnack(error.response?.data?.detail || 'Failed to promote user', 'error');
      }
      return;
    }
    if (newRole === 'app_viewer') {
      const ok = window.confirm(
        `Change ${admin.email} to App Viewer?\n\n` +
        'They will lose platform-wide access and only see the apps they are listed as a viewer on. ' +
        'They need to be a viewer on at least one app.'
      );
      if (!ok) return;
    }
    await handleUpdateRole(admin.email, newRole);
  };

  // Delete fully removes the person: their platform account (if any) AND
  // their entry in every app's viewers list — otherwise a deleted user just
  // reappears here as an App Viewer.
  const handleDelete = async (target: AdminUser) => {
    const apps = target.accessible_app_names ?? [];
    const message = target.role === 'app_viewer'
      ? `Remove ${target.email}?

They'll lose access to: ${apps.length ? apps.join(', ') : 'all their apps'}.`
      : `Remove ${target.email}?

This removes their platform access and removes them as a viewer from every app.`;
    if (!window.confirm(message)) return;
    try {
      await apiService.deleteAdmin(target.email);
      showSnack('User removed');
      loadAdmins();
    } catch (error: any) {
      showSnack(error.response?.data?.detail || 'Failed to remove admin', 'error');
    }
  };

  const handleToggleActive = async (email: string, isActive: boolean) => {
    try {
      await apiService.updateAdmin(email, { is_active: !isActive });
      showSnack(`User ${!isActive ? 'activated' : 'deactivated'}`);
      loadAdmins();
    } catch (error: any) {
      showSnack(error.response?.data?.detail || 'Failed to update status', 'error');
    }
  };

  const handleRetryEntra = async (email: string) => {
    try {
      const result = await apiService.retryEntraAssignment(email);
      showSnack(result.message, result.status === 'assigned' ? 'success' : 'error');
      loadAdmins();
    } catch (error: any) {
      showSnack(error.response?.data?.detail || 'Failed to retry SSO assignment', 'error');
    }
  };

  const handleToggleAssistant = async (target: AdminUser, next: boolean) => {
    // Optimistic UI — flip locally, revert on failure.
    setAdmins(prev => prev.map(a => a.email === target.email ? { ...a, assistant_enabled: next } : a));
    try {
      if (target.role === 'app_viewer') {
        // App viewers have no admin_users row; use the dedicated grant endpoint.
        await apiService.setAssistantAccess(target.email, next);
      } else {
        await apiService.updateAdmin(target.email, { assistant_enabled: next });
      }
      showSnack(next ? `Assistant enabled for ${target.email}` : `Assistant disabled for ${target.email}`);
    } catch (error: any) {
      setAdmins(prev => prev.map(a => a.email === target.email ? { ...a, assistant_enabled: !next } : a));
      showSnack(error.response?.data?.detail || 'Failed to update assistant access', 'error');
    }
  };

  const handleCloseCreate = () => {
    setOpenCreate(false);
    setFormData({ email: '', name: '', role: 'viewer', ms_user_id: '' });
    setDirValue(null);
    setDirOptions([]);
  };

  const canManage = user?.permissions?.can_manage_admins;
  const isSuperAdmin = user?.role === 'super_admin';

  const roleChipStyle = (role: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      super_admin: {
        bg: isDark ? alpha('#FF1A50', 0.12) : '#fee2e2',
        color: isDark ? '#FF4D7A' : '#b12b35',
      },
      admin: {
        bg: isDark ? alpha('#FFB800', 0.12) : '#fef3c7',
        color: isDark ? '#FFB800' : '#d97706',
      },
      viewer: {
        bg: isDark ? alpha('#A855F7', 0.12) : '#f3f4f6',
        color: isDark ? '#C084FC' : '#6b7280',
      },
      app_viewer: {
        bg: isDark ? alpha('#3B82F6', 0.12) : '#eff6ff',
        color: isDark ? '#60A5FA' : '#2563eb',
      },
    };
    return map[role] ?? map.viewer;
  };

  const skeletonBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.02em' }}>
            Users
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 0.25 }}>
            {admins.filter(a => a.role !== 'app_viewer').length} platform user{admins.filter(a => a.role !== 'app_viewer').length !== 1 ? 's' : ''}
            {admins.filter(a => a.role === 'app_viewer').length > 0 && ` · ${admins.filter(a => a.role === 'app_viewer').length} app viewer${admins.filter(a => a.role === 'app_viewer').length !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
        {canManage && (
          <Button data-tutorial="users-invite" variant="contained" startIcon={<AddOutlined />} onClick={() => setOpenCreate(true)}>
            Add User
          </Button>
        )}
      </Box>

      <Paper data-tutorial="users-table" sx={{ borderRadius: '16px', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">
                  <Tooltip title="Assistant chat access. Admins/super admins always have it — this toggle grants it to viewers and app viewers." arrow>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <SmartToyOutlined sx={{ fontSize: 14 }} /> Assistant
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Login</TableCell>
                {canManage && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton variant="text" width="70%" sx={{ bgcolor: skeletonBg }} />
                      <Skeleton variant="text" width="50%" sx={{ bgcolor: skeletonBg }} />
                    </TableCell>
                    <TableCell><Skeleton variant="rounded" width={90} height={24} sx={{ bgcolor: skeletonBg }} /></TableCell>
                    <TableCell><Skeleton variant="rounded" width={60} height={24} sx={{ bgcolor: skeletonBg }} /></TableCell>
                    <TableCell align="center"><Skeleton variant="rounded" width={34} height={20} sx={{ bgcolor: skeletonBg, mx: 'auto' }} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} sx={{ bgcolor: skeletonBg }} /></TableCell>
                    <TableCell><Skeleton variant="text" width={120} sx={{ bgcolor: skeletonBg }} /></TableCell>
                    {canManage && <TableCell align="right"><Skeleton variant="circular" width={28} height={28} sx={{ bgcolor: skeletonBg }} /></TableCell>}
                  </TableRow>
                ))
              ) : admins.map((admin) => {
                const rc = roleChipStyle(admin.role);
                return (
                  <TableRow key={admin.email}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box sx={{
                          width: 32, height: 32, borderRadius: '8px', flexShrink: 0,
                          bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          overflow: 'hidden',
                        }}>
                          {admin.photo_url ? (
                            <Box component="img" src={admin.photo_url} alt={admin.name}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.primary', letterSpacing: '0.02em' }}>
                              {admin.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()}
                            </Typography>
                          )}
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary' }}>
                            {admin.name}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
                            {admin.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {canManage ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          <Select
                            value={admin.role}
                            onChange={(e) => handleRoleChange(admin, e.target.value)}
                            size="small"
                            disabled={admin.email === user?.email}
                            sx={{ fontSize: '0.8rem', minWidth: 130 }}
                          >
                            <MenuItem value="super_admin">Super Admin</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                            <MenuItem value="viewer">Viewer</MenuItem>
                            <MenuItem value="app_viewer">App Viewer</MenuItem>
                          </Select>
                          {admin.role === 'app_viewer' && admin.accessible_app_names && admin.accessible_app_names.length > 0 && (
                            <Tooltip
                              title={
                                <Box>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, mb: 0.5 }}>Accessible apps:</Typography>
                                  {admin.accessible_app_names.map((name: string) => (
                                    <Typography key={name} sx={{ fontSize: '0.72rem' }}>• {name}</Typography>
                                  ))}
                                </Box>
                              }
                              arrow
                            >
                              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', cursor: 'default', width: 'fit-content' }}>
                                {admin.accessible_app_names.length} app{admin.accessible_app_names.length !== 1 ? 's' : ''}
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          <Chip
                            label={roleLabel[admin.role] || admin.role}
                            size="small"
                            sx={{
                              bgcolor: rc.bg, color: rc.color,
                              fontWeight: 600, fontSize: '0.72rem',
                              height: 24, border: 'none', width: 'fit-content',
                            }}
                          />
                          {admin.role === 'app_viewer' && admin.accessible_app_names && admin.accessible_app_names.length > 0 && (
                            <Tooltip
                              title={
                                <Box>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, mb: 0.5 }}>Accessible apps:</Typography>
                                  {admin.accessible_app_names.map((name: string) => (
                                    <Typography key={name} sx={{ fontSize: '0.72rem' }}>• {name}</Typography>
                                  ))}
                                </Box>
                              }
                              arrow
                            >
                              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', cursor: 'default', width: 'fit-content' }}>
                                {admin.accessible_app_names.length} app{admin.accessible_app_names.length !== 1 ? 's' : ''}
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                        <Chip
                          label={admin.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          clickable={canManage && admin.email !== user?.email}
                          onClick={canManage && admin.email !== user?.email
                            ? () => handleToggleActive(admin.email, admin.is_active)
                            : undefined}
                          sx={{
                            bgcolor: admin.is_active
                              ? isDark ? alpha('#00F5A0', 0.12) : '#dcfce7'
                              : isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6',
                            color: admin.is_active
                              ? isDark ? '#6FF7C8' : '#16a34a'
                              : isDark ? '#64748B' : '#6b7280',
                            fontWeight: 600,
                            fontSize: '0.72rem',
                            height: 24,
                            border: 'none',
                          }}
                        />
                        {admin.entra_status === 'failed' && (
                          <Tooltip
                            title={
                              `Azure AD assignment failed — this user may not be able to sign in ` +
                              `even though their account here looks fine.` +
                              (admin.entra_last_error ? ` (${admin.entra_last_error})` : '')
                            }
                            arrow
                          >
                            <Chip
                              label={canManage ? 'SSO issue — Retry' : 'SSO issue'}
                              size="small"
                              clickable={canManage}
                              onClick={canManage ? () => handleRetryEntra(admin.email) : undefined}
                              sx={{
                                bgcolor: isDark ? alpha('#F59E0B', 0.15) : '#FEF3C7',
                                color: isDark ? '#FBBF24' : '#B45309',
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                height: 20,
                                border: 'none',
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {(admin.role === 'super_admin' || admin.role === 'admin') ? (
                        <Tooltip title="Admins always have assistant access — no grant needed" arrow>
                          <Chip
                            label="Always on"
                            size="small"
                            sx={{
                              bgcolor: isDark ? alpha('#10B981', 0.12) : '#DCFCE7',
                              color: isDark ? '#6FF7C8' : '#059669',
                              fontWeight: 600, fontSize: '0.68rem', height: 22, border: 'none',
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title={isSuperAdmin ? '' : 'Only super admins can grant assistant access'} arrow>
                          <span>
                            <Switch
                              size="small"
                              checked={!!admin.assistant_enabled}
                              onChange={(e) => handleToggleAssistant(admin, e.target.checked)}
                              disabled={!isSuperAdmin}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                      {formatDate(admin.created_at, user?.timezone)}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                      {admin.last_login ? formatDateTime(admin.last_login, user?.timezone) : '—'}
                    </TableCell>
                    {canManage && (
                      <TableCell align="right">
                          <IconButton
                            onClick={() => handleDelete(admin)}
                            disabled={admin.email === user?.email}
                            size="small"
                            sx={{
                              color: isDark ? '#475569' : '#9ca3af',
                              '&:hover': { color: '#dc2626', bgcolor: alpha('#dc2626', 0.1) },
                              '&:disabled': { color: isDark ? '#1E293B' : '#d1d5db' },
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {!tableLoading && admins.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canManage ? 7 : 6} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    No admin users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Admin Dialog */}
      <Dialog open={openCreate} onClose={handleCloseCreate} maxWidth="xs" fullWidth>
        <DialogTitle>Add Platform User</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Autocomplete<DirUser>
            fullWidth
            options={dirOptions}
            loading={dirLoading}
            value={dirValue}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.email === b.email}
            getOptionDisabled={(o) => admins.some((a) => a.email === o.email)}
            filterOptions={(x) => x}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') searchDir(value);
            }}
            onChange={(_, value) => {
              setDirValue(value);
              setFormData(prev => ({ ...prev, email: value?.email ?? '', name: value?.name ?? '', ms_user_id: value?.id ?? '' }));
            }}
            renderOption={(props, option) => {
              const initials = option.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
              const isExisting = admins.some((a) => a.email === option.email);
              return (
                <Box component="li" {...props} sx={{ opacity: isExisting ? 0.45 : 1 }}>
                  <Avatar
                    src={option.photo ?? undefined}
                    alt={option.name}
                    sx={{
                      width: 36, height: 36, mr: 1.5, fontSize: '0.8rem', flexShrink: 0,
                      bgcolor: isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
                      color: isDark ? '#94A3B8' : '#4b5563',
                    }}
                  >
                    {!option.photo && initials}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary', lineHeight: 1.3 }}>
                      {option.name}
                      {isExisting && (
                        <Typography component="span" sx={{ ml: 1, fontSize: '0.7rem', color: 'text.secondary', fontWeight: 400 }}>
                          (already admin)
                        </Typography>
                      )}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3 }}>
                      {option.email}
                    </Typography>
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Organisation Directory"
                margin="normal"
                placeholder="Type at least 2 characters…"
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps.input,
                    startAdornment: dirValue ? (
                      <>
                        <Avatar
                          src={dirValue.photo ?? undefined}
                          alt={dirValue.name}
                          sx={{
                            width: 24, height: 24, mr: 0.75, fontSize: '0.65rem',
                            bgcolor: isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
                            color: isDark ? '#94A3B8' : '#4b5563',
                          }}
                        >
                          {!dirValue.photo && dirValue.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                        </Avatar>
                        {params.slotProps.input.startAdornment}
                      </>
                    ) : params.slotProps.input.startAdornment,
                    endAdornment: (
                      <>
                        {dirLoading && <CircularProgress size={14} />}
                        {params.slotProps.input.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
          {dirLoading && (
            <LinearProgress sx={{ borderRadius: 1, mt: -0.5, mb: 0.5 }} />
          )}

          {formData.email && dirValue && (
            <Box sx={{
              mt: 0.5, px: 1.5, py: 1,
              bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
              borderRadius: '10px',
              border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
              display: 'flex', alignItems: 'center', gap: 1.5,
            }}>
              <Avatar
                src={dirValue.photo ?? undefined}
                alt={dirValue.name}
                sx={{
                  width: 40, height: 40, fontSize: '0.85rem', flexShrink: 0,
                  bgcolor: isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
                  color: isDark ? '#94A3B8' : '#4b5563',
                }}
              >
                {!dirValue.photo && dirValue.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: '0.85rem', color: 'text.primary', fontWeight: 600, lineHeight: 1.3 }}>{formData.name}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3 }}>{formData.email}</Typography>
              </Box>
            </Box>
          )}

          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              label="Role"
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              <MenuItem value="super_admin">Super Admin</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="viewer">Viewer (read-only platform access)</MenuItem>
            </Select>
          </FormControl>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.5 }}>
            To grant app-level viewer access, use the Viewers panel inside each application.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseCreate} disabled={creating} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!formData.email || !formData.name || creating}
            startIcon={creating ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
          >
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsersPage;
